import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const GraphicsDesignPage = () => {
    const [showPortfolioMenu, setShowPortfolioMenu] = useState(false);
    const navigate = useNavigate();

    // Refs for lazy-loading sections and images
    const heroImageRef = useRef(null);
    const shalomLogoRef = useRef(null);
    const shalomBgRef = useRef(null);
    const [heroImageLoaded, setHeroImageLoaded] = useState(false);
    const [shalomLogoLoaded, setShalomLogoLoaded] = useState(false);
    const [shalomBgLoaded, setShalomBgLoaded] = useState(false);

    // Intersection Observer logic for fade-in animations and image loading
    const observedSections = useRef(new Set());
    const setSectionRef = useCallback((node) => {
        if (node) {
            observedSections.current.add(node);
        }
    }, []);

    const handleSectionIntersect = useCallback((entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                entry.target.classList.add('fade-in-up-active');
                if (entry.target.currentObserver) {
                    entry.target.currentObserver.unobserve(entry.target);
                }
            }
        });
    }, []);

    useEffect(() => {
        const observer = new IntersectionObserver(handleSectionIntersect, {
            root: null,
            rootMargin: '0px',
            threshold: 0.1
        });
        observedSections.current.forEach(section => {
            if (section) {
                observer.observe(section);
                section.currentObserver = observer;
            }
        });
        return () => {
            observedSections.current.forEach(section => {
                if (section && section.currentObserver) {
                    section.currentObserver.unobserve(section);
                }
            });
            observer.disconnect();
            observedSections.current.clear();
        };
    }, [handleSectionIntersect]);

    // Use a separate observer for lazy-loading images
    useEffect(() => {
        const imageObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        const target = entry.target;
                        if (target === heroImageRef.current) {
                            setHeroImageLoaded(true);
                        } else if (target === shalomLogoRef.current) {
                            setShalomLogoLoaded(true);
                        } else if (target === shalomBgRef.current) {
                            setShalomBgLoaded(true);
                        }
                        imageObserver.unobserve(target);
                    }
                });
            },
            { root: null, rootMargin: '200px', threshold: 0.1 }
        );

        if (heroImageRef.current) imageObserver.observe(heroImageRef.current);
        if (shalomLogoRef.current) imageObserver.observe(shalomLogoRef.current);
        if (shalomBgRef.current) imageObserver.observe(shalomBgRef.current);

        return () => {
            if (heroImageRef.current) imageObserver.unobserve(heroImageRef.current);
            if (shalomLogoRef.current) imageObserver.unobserve(shalomLogoRef.current);
            if (shalomBgRef.current) imageObserver.unobserve(shalomBgRef.current);
        };
    }, []);

    return (
        <div className="min-h-screen bg-[#0A1128] text-[#F8F8F8] font-sans overflow-x-hidden">

            {/* Global Styles for animations (same as App.js for consistency) */}
            <style>
                {`
                .fade-in-up { opacity: 0; transform: translateY(20px); transition: opacity 0.8s ease-out, transform 0.8s ease-out; }
                .fade-in-up-active { opacity: 1; transform: translateY(0); }
                .animate-fade-in-down {
                    animation: fadeInDown 0.3s ease-out forwards;
                }
                @keyframes fadeInDown {
                    from { opacity: 0; transform: translateY(-10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                `}
            </style>

            {/* Portfolio Selection Button at the top right (fixed position) */}
            <div className="fixed top-24 right-8 z-20">
                <div className="relative">
                    <button
                        onClick={() => setShowPortfolioMenu(!showPortfolioMenu)}
                        className="px-6 py-2 bg-[#4CAF50] text-[#0A1128] font-bold text-lg rounded-full shadow-2xl hover:bg-opacity-90 transition duration-300 transform hover:scale-105 flex items-center justify-center space-x-2"
                    >
                        <span>View Portfolios</span>
                        <svg
                            className={`w-5 h-5 ml-2 transition-transform duration-300 ${showPortfolioMenu ? 'rotate-180' : 'rotate-0'}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                        </svg>
                    </button>

                    {/* Portfolio Options Menu (Dropdown) */}
                    {showPortfolioMenu && (
                        <div className="absolute top-full mt-2 right-0 bg-[#1C2C59] text-[#F8F8F8] rounded-xl shadow-xl w-64 overflow-hidden z-30 animate-fade-in-down">
                            <Link
                                to="/graphics-design/poster-gallery"
                                className="block w-full text-left px-6 py-4 text-lg hover:bg-[#0A1128] border-b border-[#2A3A69] transition duration-200 rounded-t-xl"
                                onClick={() => setShowPortfolioMenu(false)}
                            >
                                Print & Marketing (Posters)
                            </Link>
                            <Link
                                to="/graphics-design/video-editing"
                                className="block w-full text-left px-6 py-4 text-lg hover:bg-[#0A1128] border-b border-[#2A3A69] transition duration-200"
                                onClick={() => setShowPortfolioMenu(false)}
                            >
                                Video Editing & Production
                            </Link>
                            <Link
                                to="/graphics-design/other-portfolio"
                                className="block w-full text-left px-6 py-4 text-lg hover:bg-[#0A1128] transition duration-200"
                                onClick={() => setShowPortfolioMenu(false)}
                            >
                                Other Portfolio (Coming Soon!)
                            </Link>
                        </div>
                    )}
                </div>
            </div>

            {/* Section 1: Hero/Introduction - With Image Background */}
            <section
                className="relative py-20 text-center px-6 bg-cover bg-center bg-no-repeat"
                style={{ backgroundImage: heroImageLoaded ? `url('https://i.imgur.com/fxGruEt.jpeg')` : 'none', backgroundColor: '#0A1128' }}
                ref={heroImageRef}
            >
                {/* Overlay for better text readability - Changed opacity to 20% */}
                <div className="absolute inset-0 bg-black bg-opacity-20"></div>
                <div className="relative z-10 container mx-auto flex flex-col items-center justify-center">
                    {/* Added a div for the rounded black overlay */}
                    <div className="bg-black bg-opacity-60 p-8 rounded-lg max-w-4xl w-full">
                        <h1 className="text-5xl md:text-6xl font-extrabold text-[#C9B072] mb-6">
                            Graphics & Design Services
                        </h1>
                        <p className="text-xl md:text-2xl text-[#F8F8F8] max-w-3xl mx-auto mb-10">
                            Transform your vision into stunning visual realities. Our graphic design experts craft compelling logos, engaging marketing materials, and cohesive brand identities that resonate with your audience.
                        </p>
                        <Link to="/get-a-quote" className="px-8 py-3 bg-[#4CAF50] text-[#F8F8F8] font-semibold text-lg rounded-full shadow-lg transition duration-300 transform hover:scale-105 hover:bg-opacity-90 inline-flex items-center justify-content-center">
                            Get a Design Quote
                        </Link>
                    </div>
                </div>
            </section>

            ---

            {/* Section 2: Core Services - Whitish Background */}
            <section className="py-20 bg-gray-100 border-t border-[#C9B072] text-center px-6">
                <div className="container mx-auto">
                    <h2 className="text-4xl sm:text-5xl font-extrabold text-[#0A1128] mb-12 fade-in-up" ref={setSectionRef}>
                        What We Offer
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
                        {[
                            { title: "Logo & Brand Identity", delay: "0.1s", text: "Crafting unique and memorable logos, brand guidelines, and complete visual identities that tell your story.", iconText: "✨" },
                            { title: "Web & UI/UX Design", delay: "0.2s", text: "Designing intuitive, engaging, and responsive interfaces for websites and mobile applications.", iconText: "💻" },
                            { title: "Print & Marketing Materials", delay: "0.3s", text: "From brochures and flyers to business cards and banners, we ensure your print materials stand out.", iconText: "📄" },
                            { title: "Social Media Graphics", delay: "0.4s", text: "Engaging visuals tailored for all social media platforms to boost your online presence.", iconText: "📱" },
                            { title: "Custom Illustrations", delay: "0.5s", text: "Unique illustrations to add personality and visual appeal to your website, marketing, or products.", iconText: "🎨" },
                            { title: "Video Editing & Motion Graphics", delay: "0.6s", text: "Professional video editing and dynamic motion graphics to tell your story effectively.", iconText: "🎬" },
                        ].map(({ title, delay, text, iconText }, idx) => (
                            <div
                                key={idx}
                                className="bg-white rounded-xl p-6 flex flex-col items-center text-center shadow-lg fade-in-up transform transition-transform duration-300 hover:scale-105"
                                ref={setSectionRef}
                                style={{ animationDelay: delay }}
                            >
                                <div className="p-4 bg-[#C9B072] text-[#0A1128] rounded-full mb-4 inline-flex items-center justify-content-center text-5xl">
                                    {iconText}
                                </div>
                                <h3 className="text-2xl font-bold text-[#0A1128] mb-2">{title}</h3>
                                <p className="text-gray-700 text-lg leading-relaxed">{text}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            ---

            {/* NEW Section: Collaborative Team Services - Blue Background */}
            <section className="py-20 bg-[#0A1128] border-t border-[#C9B072] text-center px-6">
                <div className="container mx-auto">
                    <h2 className="text-4xl sm:text-5xl font-extrabold text-[#F8F8F8] mb-12 fade-in-up" ref={setSectionRef}>
                        Collaborating with Shalom Creative Studios
                    </h2>
                    <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-center gap-10 mb-12">
                        <div className="md:w-1/3 flex justify-center fade-in-up" ref={setSectionRef}>
                            <img
                                src={shalomLogoLoaded ? "https://i.imgur.com/ewWcFiQ.png" : 'https://placehold.co/200x200/0A1128/C9B072?text=Shalom+Logo'}
                                alt="Shalom Creative Studios Logo"
                                className="w-48 h-48 md:w-64 md:h-64 object-contain rounded-full shadow-xl p-2 bg-white"
                                onError={(e) => { e.target.onerror = null; e.target.src = 'https://placehold.co/200x200/0A1128/C9B072?text=Shalom+Logo'; }}
                                ref={shalomLogoRef}
                            />
                        </div>
                        <div className="md:w-2/3 text-center md:text-left fade-in-up" ref={setSectionRef}>
                            <p className="text-xl text-[#CCD2E3] leading-relaxed mb-6">
                                We're proud to partner with **Shalom Creative Studios**, a leading team specializing in high-quality visual and audio production. Their expertise complements our design services, offering you a complete solution for your media needs.
                            </p>
                            <p className="text-xl text-[#CCD2E3] leading-relaxed">
                                From captivating photography and dynamic video production to seamless livestreaming and professional Public Address (PA) system rentals, Shalom Creative Studios ensures your events and content are captured and delivered with excellence.
                            </p>
                        </div>
                    </div>

                    {/* Their Specialized Services section with new background */}
                    <div
                        className="relative py-12 px-6 bg-cover bg-center bg-no-repeat rounded-xl overflow-hidden"
                        style={{ backgroundImage: shalomBgLoaded ? `url('https://i.imgur.com/vyiPWaQ.jpeg')` : 'none', backgroundColor: '#0A1128' }}
                        ref={shalomBgRef}
                    >
                        <div className="absolute inset-0 bg-black bg-opacity-20 z-0 rounded-xl"></div>
                        <h3 className="relative z-10 text-3xl font-bold text-[#C9B072] mb-8 fade-in-up" ref={setSectionRef}>
                            Their Specialized Services
                        </h3>
                        <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
                            {[
                                { title: "Professional Photography", delay: "0.1s", text: "High-resolution photos for events, products, portraits, and corporate needs.", iconText: "📸" },
                                { title: "Videography & Production", delay: "0.2s", text: "Full-scale video production from concept to final cut, including commercials and documentaries.", iconText: "🎥" },
                                { title: "Live Streaming Solutions", delay: "0.3s", text: "Seamless live broadcasts for events, webinars, and conferences to reach a wider audience.", iconText: "📡" },
                                { title: "PA System Rental", delay: "0.4s", text: "High-quality public address systems for crystal-clear audio at any event size.", iconText: "🔊" },
                            ].map(({ title, delay, text, iconText }, idx) => (
                                <div
                                    key={idx}
                                    className="bg-[#1A2238] rounded-xl p-6 flex flex-col items-center text-center shadow-xl border border-[#4CAF50] fade-in-up transform transition-transform duration-300 hover:scale-105"
                                    ref={setSectionRef}
                                    style={{ animationDelay: delay }}
                                >
                                    <div className="p-4 bg-[#4CAF50] text-[#0A1128] rounded-full mb-4 inline-flex items-center justify-content-center text-5xl">
                                        {iconText}
                                    </div>
                                    <h4 className="text-2xl font-bold text-[#F8F8F8] mb-2">{title}</h4>
                                    <p className="text-[#CCD2E3]">{text}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* NEW: Contact Shalom Button */}
                    <div className="flex justify-center mt-8 fade-in-up" ref={setSectionRef}>
                        <Link
                            to="/contact-us"
                            className="px-10 py-4 bg-[#C9B072] text-[#0A1128] font-bold text-xl rounded-full shadow-2xl transition duration-300 transform hover:scale-105 hover:bg-opacity-90 inline-flex items-center justify-content-center"
                        >
                            Contact Shalom Creative Studios
                        </Link>
                    </div>
                </div>
            </section>

            ---

            {/* Section 3: Image Tools (now Section 4) - Whitish Background */}
            <section className="py-20 bg-gray-100 border-t border-[#C9B072] text-center px-6">
                <div className="container mx-auto max-w-3xl">
                    <h2 className="text-4xl sm:text-5xl font-extrabold text-[#0A1128] mb-8 fade-in-up" ref={setSectionRef}>
                        Need a Quick Image Fix?
                    </h2>
                    <p className="text-xl text-[#333333] mb-10 fade-in-up" ref={setSectionRef}>
                        Our integrated image tools allow you to quickly remove backgrounds, resize, and optimize images directly. Try it out for free!
                    </p>
                    <Link
                        to="/graphics-design/image-tools"
                        className="px-10 py-4 bg-[#C9B072] text-[#0A1128] font-bold text-xl rounded-full shadow-2xl transition duration-300 transform hover:scale-105 hover:bg-opacity-90 inline-flex items-center justify-content-center fade-in-up" ref={setSectionRef}
                    >
                        Access Image Tools
                    </Link>
                </div>
            </section>

            ---

            {/* Section 4: Call to Action (now Section 5) - Blue Background */}
            <section className="py-20 bg-[#0A1128] border-t border-[#C9B072] text-center px-6">
                <div className="container mx-auto max-w-3xl">
                    <h2 className="text-4xl sm:text-5xl font-extrabold text-[#F8F8F8] mb-8 fade-in-up" ref={setSectionRef}>
                        Ready to Elevate Your Brand?
                    </h2>
                    <p className="text-xl text-[#CCD2E3] mb-10 fade-in-up" ref={setSectionRef}>
                        Let's create something extraordinary together. Contact us today to discuss your project.
                    </p>
                    <Link to="/contact-us" className="px-12 py-4 bg-[#4CAF50] text-[#F8F8F8] font-bold text-xl rounded-full shadow-2xl transition duration-300 transform hover:scale-105 hover:bg-opacity-90 animate-pulse inline-flex items-center justify-content-center" ref={setSectionRef}>
                        Contact Our Team
                    </Link>
                </div>
            </section>

        </div>
    );
};

export default GraphicsDesignPage;
